/*
Species index loader — mirrors Android SpeciesIndex.kt.

The on-disk format (`/data/species-index.json`) is an array produced by
`tools/build_dataset.py`:

    [{ "i": 7, "g": "Platanus", "e": "x hispanica",
       "nc": "Platane", "nv": "Platane commun", "n": 1 }, ...]
    [{ "i": 865, "g": "Platanus", "e": "n. sp.",
       "u": true, "nv": "Platane (Platanus n. sp.)" }, ...]

- `i`  : stable integer index — referenced by CaptureEntity.speciesIndex and
        by feature.properties.sk in the geojson. Never reorder; preserved
        across rebuilds by the build script.
- `g`  : genus (Latin, capitalized).
- `e`  : species epithet (Latin, lowercase).
- `nc` : nom commun (legacy v1.0 French label, broad — "Platane"). Kept for
        rétrocompat. Optional.
- `nv` : nom vernaculaire unique (v1.1.0 cascade Wikidata P1843 →
        Wikipedia FR redirects → editorial overrides — "Platane commun").
        This is the canonical user-facing label when present. Optional.
- `n`  : Pokédex rank (1-indexed, set only on identified species, ordered
        by Paris count descending and frozen at build time). Optional.
- `u`  : true on `(G, sp.)` placeholder rows that absorb captures whose
        species couldn't be identified at the source. The Catalogue hides
        these from the species grids; only the GenreDetailScreen surfaces
        them, in a separate "captures indéterminées" gallery. Optional.

This loader builds two memoized maps in one pass:
- forward:  "<genre>__<espece>" -> int           (used by tools/import paths)
- reverse:  int -> SpeciesName                  (used by the Arboretum)
*/

import { fetchJson } from './fetchJson'

const kAssetPath = '/data/species-index.json'  // canonical location served by the SW
const kNonSpecifie = 'Non spécifié'  // v1.3.1 — degenerate pseudo-genus excluded from allGenres() (parity Android)

export interface SpeciesName
{
	genre: string
	espece: string
	commonName: string | null  // legacy `nc` — fallback under `nv` in the cascade
	vernacularName: string | null  // v1.1.0 `nv` — preferred user-facing label
	pokedexNumber: number | null  // v1.1.0 `n` — null on `unknownSpecies` rows
	unknownSpecies: boolean  // v1.1.0 `u: true` — (G, sp.) placeholder row
}

/*
v1.3.1 canonical filter — an "active" species is identified AND has a
pokedexNumber. Mirror Android `SpeciesIndex.kt`:
    val SpeciesEntry.isActive: Boolean
        get() = !unknownSpecies && pokedexNumber != null

Two kinds of rows are filtered out :
- `unknownSpecies` placeholders `(G, sp.)` — surfaced only by
  GenreDetailScreen as "captures indéterminées".
- Zombie rows : identified species without a `pokedexNumber`, count=0 in
  the current OpenData snapshot (e.g. Eriolobus trilobata, Malus communis,
  Styphnolobium japonica, ~16 entries at v1.3.2). They exist in the
  catalogue for backward-compat with older datasets but should not appear
  in the Arboretum or genre details — no user can ever capture them.
*/
export function isActiveSpecies(inName: SpeciesName): boolean
{
	return !inName.unknownSpecies && inName.pokedexNumber !== null
}

interface RawEntry
{
	i: number
	g: string
	e: string
	nc?: string
	nv?: string
	n?: number
	u?: boolean
}

let fForward: Map<string, number> | null = null  // memoized "genre__espece" -> int
let fReverse: Map<number, SpeciesName> | null = null  // memoized int -> name pair

/*
Ensure the species index is loaded. Idempotent — only the first call hits
the network. Builds both forward and reverse maps in a single pass.
Failure leaves both maps null and rethrows so the UI can surface the error.
*/
export async function ensureSpeciesIndex(): Promise<Map<string, number>>
{
	if (fForward) return fForward
	const vRaw = await fetchJson<RawEntry[]>(kAssetPath)  // typed-as array; throws on bad JSON
	const vForward = new Map<string, number>()
	const vReverse = new Map<number, SpeciesName>()
	for (const vEntry of vRaw)
	{
		vForward.set(`${vEntry.g}__${vEntry.e}`, vEntry.i)
		vReverse.set(vEntry.i, {
			genre: vEntry.g,
			espece: vEntry.e,
			commonName: vEntry.nc && vEntry.nc.length > 0 ? vEntry.nc : null,
			vernacularName: vEntry.nv && vEntry.nv.length > 0 ? vEntry.nv : null,
			pokedexNumber: typeof vEntry.n === 'number' && vEntry.n > 0 ? vEntry.n : null,
			unknownSpecies: vEntry.u === true,
		})
	}
	fForward = vForward
	fReverse = vReverse
	return fForward
}

/*
Look up the int index for a (genre, espece) pair. Returns null if the
species is not in the index — caller decides whether to treat this as a
soft miss or a hard data error.
*/
export function speciesIndexFor(inGenre: string, inEspece: string): number | null
{
	if (!fForward) return null
	const vKey = `${inGenre}__${inEspece}`  // composite key matching the build script
	return fForward.get(vKey) ?? null
}

/*
Reverse lookup: int index -> SpeciesName. Used by the Arboretum, capture
detail modal, fly-to deep-links, and any other surface that holds a `sk`
and needs to label it.
*/
export function speciesNameFor(inIndex: number): SpeciesName | null
{
	if (!fReverse) return null
	return fReverse.get(inIndex) ?? null
}

/*
Iterate every loaded species. Returns an empty array before the index
is loaded; caller is expected to `await ensureSpeciesIndex()` first.
Used by the Arboretum Catalogue grids and the GenreDetailScreen
mini-catalogue (both filter out `unknownSpecies` rows at the call site).
*/
export function allSpecies(): { sk: number; name: SpeciesName }[]
{
	if (!fReverse) return []
	const vOut: { sk: number; name: SpeciesName }[] = []
	for (const [vSk, vName] of fReverse) vOut.push({ sk: vSk, name: vName })
	return vOut
}

/*
Cascade matching Android `displayNomCommun`: `nv` (v1.1.0 unique
vernacular) → `nc` (v1.0 broad common name) → "{genre} {espece}"
binomial. Used as the primary user-facing label everywhere a species
is rendered. Caller is expected to have a non-null SpeciesName — pass
the result of `speciesNameFor(sk)` only when it's non-null, otherwise
render "Espèce inconnue" upstream.
*/
export function displayName(inName: SpeciesName): string
{
	if (inName.vernacularName) return inName.vernacularName
	if (inName.commonName) return inName.commonName
	return formatLatin(inName)
}

/*
Latin binomial "Genus epithet" — preserves the build-script casing
(genus title-case, epithet lower-case). Falls back to the genus alone
when the epithet is empty (rare in OpenData). Exposed because several
screens need to render the binomial separately as a subtitle under
the vernacular display name.
*/
export function formatLatin(inName: SpeciesName): string
{
	const vEspece = inName.espece.length > 0 ? ` ${inName.espece}` : ''
	return `${inName.genre}${vEspece}`
}

/*
Genre-level extensions — helpers for the badge catalogue (Familier des
[genre]) and the Profile ProgressionCard. Mirror Android
`SpeciesIndex.kt` post-v1.1.0.

All four caches below are lazily populated on first access and
invalidated when `ensureSpeciesIndex` rebuilds `fReverse` (the load
is idempotent in practice — only happens once per session — so we
keep the cache invariant simple : reset to null when fReverse is
replaced).
*/

let fAllGenres: string[] | null = null  // memoized sorted list of all genres (incl. unknown-only genres? cf. Android allGenres)
let fGenreCount: Map<string, number> | null = null  // memoized genre -> active-species count (v1.3.1 isActive filter)
let fSksByGenre: Map<string, Set<number>> | null = null  // memoized genre -> Set<sk active> (v1.3.1 isActive filter)
let fSksAllByGenre: Map<string, Set<number>> | null = null  // memoized genre -> Set<sk> all rows incl. unknownSpecies (used by genreHasAnyCapture)

function buildGenreCaches(): void
{
	if (!fReverse) return
	const vAll = new Set<string>()
	const vCount = new Map<string, number>()
	const vSksByGenre = new Map<string, Set<number>>()
	const vSksAllByGenre = new Map<string, Set<number>>()
	for (const [vSk, vName] of fReverse)
	{
		vAll.add(vName.genre)
		let vAllSet = vSksAllByGenre.get(vName.genre)
		if (!vAllSet) { vAllSet = new Set<number>(); vSksAllByGenre.set(vName.genre, vAllSet) }
		vAllSet.add(vSk)
		if (!vName.unknownSpecies && isActiveSpecies(vName))
		{
			// v1.3.1 : zombie rows (identified, !unknownSpecies, pokedexNumber===null)
			// are dropped from genreCount/capturedCountInGenre. They remain in the
			// catalogue for backward-compat with older datasets but no surface
			// counts them as a target.
			vCount.set(vName.genre, (vCount.get(vName.genre) ?? 0) + 1)
			let vSet = vSksByGenre.get(vName.genre)
			if (!vSet) { vSet = new Set<number>(); vSksByGenre.set(vName.genre, vSet) }
			vSet.add(vSk)
		}
	}
	// v1.3.1 : exclut le pseudo-genre dégénéré « Non spécifié » du
	// dénominateur de progression (parité Android `genresAllUseful`).
	// Les autres caches (genreCount, sksByGenre, sksAllByGenre) le
	// conservent pour qu'une capture mappée à un sk « Non spécifié »
	// reste résolvable côté speciesNameFor / genreOf, mais aucune surface
	// utilisateur ne l'expose comme un genre découvrable.
	fAllGenres = Array.from(vAll).filter((g) => g !== kNonSpecifie).sort((a, b) => a.localeCompare(b))
	fGenreCount = vCount
	fSksByGenre = vSksByGenre
	fSksAllByGenre = vSksAllByGenre
}

/*
v1.3.1 — All "useful" genres present in the species index, sorted
lexicographically. Includes genres that only carry an `unknownSpecies`
row (a genre present in captures but with zero identified species —
Genista, Vitex, Ziziphus at v1.3.2). Excludes the degenerate
« Non spécifié » pseudo-genus which catches OpenData entries without a
clear genus (3 unknownSpecies rows at v1.3.2). Mirror Android
`SpeciesIndex.allGenres()` (which exposes `genresAllUseful`, count =
204 at v1.3.2). Source of truth for the « X / 204 genres découverts »
denominator on Arboretum + Profile. Caller is expected to
`await ensureSpeciesIndex()` first.
*/
export function allGenres(): string[]
{
	if (!fAllGenres) buildGenreCaches()
	return fAllGenres ?? []
}

/*
Number of *identified* species in a given genre (excludes the
`unknownSpecies` placeholder row). Returns 0 for unknown genres or
genres that only carry an unknown row.

Used by :
- `familierGenres()` to filter genres eligible for the « Familier
  des [genre] » badge (≥ GENRE_FAMILIER_MIN_SPECIES = 7).
- `evaluateFamilierGenre()` as the unlock target.
*/
export function genreCount(inGenre: string): number
{
	if (!fGenreCount) buildGenreCaches()
	return fGenreCount?.get(inGenre) ?? 0
}

/*
Number of *identified* species in the given genre that appear in
`inCapturedSks` (intersection cardinality). Used by
`evaluateFamilierGenre` : unlock when this == genreCount(genre).
*/
export function capturedCountInGenre(inGenre: string, inCapturedSks: ReadonlySet<number>): number
{
	if (!fSksByGenre) buildGenreCaches()
	const vSet = fSksByGenre?.get(inGenre)
	if (!vSet) return 0
	let vCount = 0
	for (const vSk of vSet) if (inCapturedSks.has(vSk)) vCount++
	return vCount
}

/*
v1.5.0 — le set d'espèces du filtre carte « Tout le genre » : l'entrée
`(G, sp.)` du genre si elle existe (« ce que j'ai à résoudre ») ∪ chaque
espèce identifiée ACTIVE du genre déjà capturée (« mes trophées »).
Mirror Android `SpeciesIndex.genreFilterSet` :
    sksByGenre[genre].filter { entry.unknownSpecies ||
        (entry.isActive && sk in capturedSks) }
Partagé entre le bouton « Voir sur la carte » de GenreDetailScreen et
les filtres rapides de la sheet d'un pin (MapScreen). Peut être vide
(genre sans (G, sp.) ni capture).
*/
export function genreFilterSet(inGenre: string, inCapturedSks: ReadonlySet<number>): number[]
{
	if (!fSksAllByGenre) buildGenreCaches()
	const vAll = fSksAllByGenre?.get(inGenre)
	if (!vAll || !fReverse) return []
	const vOut: number[] = []
	for (const vSk of vAll)
	{
		const vName = fReverse.get(vSk)
		if (!vName) continue
		if (vName.unknownSpecies || (isActiveSpecies(vName) && inCapturedSks.has(vSk))) vOut.push(vSk)
	}
	return vOut
}

/*
Genre of the given sk, or null if the sk is unknown. Convenience
wrapper around `speciesNameFor(sk)?.genre`.
*/
export function genreOf(inSk: number): string | null
{
	const vName = fReverse?.get(inSk)
	return vName?.genre ?? null
}

/*
v1.3.1 : returns true ssi at least one `sk` of `inGenre` (identified OR
unknownSpecies) appears in `inCapturedSks`. "Touched" semantics — used by
ProfileScreen `Genres découverts` bar and by ArboretumScreen Catalogue
"Par genre" chapter-unlock logic.

Mirror Android `SpeciesIndex.kt`:
    fun genreHasAnyCapture(genre: String, capturedSks: Set<Int>): Boolean
        = sksByGenre(genre).any { it in capturedSks }

The Android version returns true for any sk of the genre being captured,
including the `(G, sp.)` placeholder — a capture of `Genista sp.` should
make the genre count as touched. We mirror that by using the
`fSksAllByGenre` cache which includes both active species and the
unknownSpecies placeholder.
*/
export function genreHasAnyCapture(inGenre: string, inCapturedSks: ReadonlySet<number>): boolean
{
	if (!fSksAllByGenre) buildGenreCaches()
	const vSet = fSksAllByGenre?.get(inGenre)
	if (!vSet) return false
	for (const vSk of vSet) if (inCapturedSks.has(vSk)) return true
	return false
}

/*
Auto-déblocage genre-based des fiches `(G, sp.)` : un sk `unknownSpecies`
est considéré découvert dès qu'un sk frère du même genre est capturé. Les
sks identifiés (non `unknownSpecies`) sont découverts au sens strict
(présents dans `inCapturedSks`).

Mirror Android `SpeciesIndex.isDiscovered`. Pilote la coloration des pins
non-remarquables sur la carte ET la révélation de la fiche détail — capturer
un membre d'une espèce débloque toute l'espèce.
*/
export function isDiscovered(inSk: number, inCapturedSks: ReadonlySet<number>): boolean
{
	if (inCapturedSks.has(inSk)) return true
	const vName = fReverse?.get(inSk)
	if (!vName || !vName.unknownSpecies) return false
	if (!fSksAllByGenre) buildGenreCaches()
	const vFreres = fSksAllByGenre?.get(vName.genre)
	if (!vFreres) return false
	for (const vFrere of vFreres) if (vFrere !== inSk && inCapturedSks.has(vFrere)) return true
	return false
}

/*
Étend `inCaptured` avec les sks `unknownSpecies` dont le genre porte au moins
une capture (sp. ou identifiée). Sert à la coloration de la carte : si
l'utilisateur a capturé `Tilia cordata`, tous les pins `Tilia sp.` doivent
passer au vert — alignement avec l'auto-déblocage genre-based de
`isDiscovered`.

Mirror Android `SpeciesIndex.effectivelyCapturedSpecies`. Retourne un Set
neuf (n'altère pas l'entrée). Renvoie une copie même quand rien n'est ajouté,
pour un type de retour stable côté appelants.
*/
export function effectivelyCapturedSpecies(inCaptured: ReadonlySet<number>): Set<number>
{
	if (inCaptured.size === 0) return new Set(inCaptured)
	if (!fReverse) return new Set(inCaptured)
	if (!fSksAllByGenre) buildGenreCaches()
	const vOut = new Set(inCaptured)
	const vCapturedGenres = new Set<string>()
	for (const vSk of inCaptured)
	{
		const vGenre = fReverse.get(vSk)?.genre
		if (vGenre) vCapturedGenres.add(vGenre)
	}
	for (const vGenre of vCapturedGenres)
	{
		const vFreres = fSksAllByGenre?.get(vGenre)
		if (!vFreres) continue
		for (const vSk of vFreres)
		{
			if (fReverse.get(vSk)?.unknownSpecies === true) vOut.add(vSk)
		}
	}
	return vOut
}
